import { useGenerateLogEntriesAndListenForPushMessageResponse } from '@monorepo/logs/src/components/LogEntriesReviewModal/hooks/useGenerateLogEntriesAndListenForPushMessageResponse';
import { USAGE_TRACKING_PREFIX } from '@monorepo/shared/constants/usageTracking';
import { LogEntry } from '@monorepo/shared/componentsV2/logs/types';
import { useLogEntryFields } from '@monorepo/shared/hooks/logs/useLogFields';
import {
  FileInputHandlerMode,
  useFileInputHandler,
} from '@monorepo/shared/hooks/useFileInputHandler';
import React, { useCallback, useMemo, useState } from 'react';
import { LogEntriesFormValues } from './conversionHelpers';
import { useCloseWithConfirmation } from './hooks/useCloseWithConfirmation';
import { useFileStatusText } from './hooks/useFileStatusText';
import { useHandleMapLogEntryFormValuesIntoLogEntries } from './hooks/useHandleMapLogEntryFormValuesIntoLogEntries';
import { useLogEntriesReviewModalNavigation } from './hooks/useLogEntriesReviewModalNavigation';
import { useLogEntriesSubmission } from './hooks/useLogEntriesSubmission';
import { useModalActionLeftButtons } from './hooks/useModalActionLeftButtons';
import { useSelectedFile } from './hooks/useSelectedFile';
import { useUploadedFiles } from './hooks/useUploadedFiles';
import { LogEntriesEditManyModalPage } from './pages/LogEntriesEditManyModalPage';
import { LogEntriesFileUploadInputModalPage } from './pages/LogEntriesFileUploadInputModalPage';
import { LogEntriesTableReviewModalPage } from './pages/LogEntriesTableReviewModalPage';
import { Pages } from './shared';
import { StyledModalWizard } from './styled';
import { validateLogEntriesFormValues } from './utils/validateLogEntriesFormValues';

type LogEntriesImageUploadModalProps = {
  logId: string;
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  projectId: string;
  isReadOnly?: boolean;
};

export function LogEntriesImageUploadModal({
  logId,
  isOpen,
  onClose,
  organizationId,
  projectId,
  isReadOnly = false,
}: LogEntriesImageUploadModalProps) {
  const [showErrorOverlay, setShowErrorOverlay] = useState(false);
  const [logEntryIDsToDelete, setLogEntryIDsToDelete] = useState<string[]>([]);

  const { logFields } = useLogEntryFields({
    organizationId,
    logId,
  });
  const validate = useCallback(
    (values: LogEntriesFormValues) =>
      validateLogEntriesFormValues(values, logFields),
    [logFields],
  );

  const { fileData, addFiles, removeFileData } = useFileInputHandler({
    fileSizeLimitInMb: 10,
    mode: FileInputHandlerMode.Append,
  });

  const {
    uploadedFiles,
    isProcessingSomeFiles,
    areAllFilesCompletedOrHaveErrors,
    areAnyFilesProcessingAndNotReady,
    hasAnyErrorFiles,
    areMultipleReadyFilesOrProcessing,
    statusCounts,
    updateLogEntriesForFile,
    fileCompleted,
    fileIsReady,
    fileErrored,
  } = useUploadedFiles(fileData);

  const { handleUploadLogEntriesImage } =
    useGenerateLogEntriesAndListenForPushMessageResponse({
      organizationId,
      projectId,
      logId,
      fileIsReady,
      fileErrored,
    });

  const selectedFile = useSelectedFile(uploadedFiles);

  const handleMapLogEntryFormValuesIntoLogEntries =
    useHandleMapLogEntryFormValuesIntoLogEntries({
      organizationId,
      logId,
      projectId,
      isOpen,
      logEntries: selectedFile?.logEntries || [],
    });

  const { handleSubmit, isSubmitting, isFileSubmitted, clearFileSubmitted } =
    useLogEntriesSubmission({
      organizationId,
      projectId,
      logId,
      selectedFile,
      logFields,
      fileCompleted,
    });

  const { currentPageNumber, navigateToFileUploadErrorPage, onNextPage } =
    useLogEntriesReviewModalNavigation({
      isFileSubmitted,
      clearFileSubmitted,
      areAllFilesCompletedOrHaveErrors,
      hasAnyErrorFiles,
      onClose,
      setShowErrorOverlay,
      selectedFile,
      updateLogEntriesForFile,
      handleMapLogEntryFormValuesIntoLogEntries,
    });

  const { getFileStatusText } = useFileStatusText({
    statusCounts,
    totalFilesUploaded: uploadedFiles.size,
    currentPageNumber,
    areAnyFilesProcessingAndNotReady,
  });

  const hasGeneratedLogEntries = !!selectedFile?.logEntries?.length;
  const { onCloseWithConfirmation } = useCloseWithConfirmation({
    requiresConfirmation:
      !isReadOnly && (isProcessingSomeFiles || hasGeneratedLogEntries),
    unsavedWorkMessage: hasGeneratedLogEntries
      ? 'The Log entries have not been submitted.'
      : 'Your files have not finished processing.',
    onClose,
  });

  const setSelectedFileLogEntries = useCallback(
    (logEntries: LogEntry[]) => {
      if (!selectedFile?.requestId) return;
      updateLogEntriesForFile(selectedFile.requestId, logEntries);
    },
    [selectedFile?.requestId, updateLogEntriesForFile],
  );

  const { renderModalActionLeftButtons, logEntryIndex, setLogEntryIndex } =
    useModalActionLeftButtons({
      logEntries: selectedFile?.logEntries,
      setLogEntries: setSelectedFileLogEntries,
      logEntryIDsToDelete,
      setLogEntryIDsToDelete,
      isReadOnly,
      deleteConfirmationDescription:
        'This will delete the data for this entry, so it will not be uploaded with the rest of the entries',
      entryFormPageNumber: Pages.EDIT_ENTRIES_PAGE,
      reviewPageNumber: Pages.REVIEW_PAGE,
    });

  const wizardPages = useMemo(
    () => [
      <LogEntriesFileUploadInputModalPage
        uploadedFiles={uploadedFiles}
        addFiles={addFiles}
        handleUploadLogEntriesImage={handleUploadLogEntriesImage}
        removeFileData={removeFileData}
        showErrorOverlay={showErrorOverlay}
        setShowErrorOverlay={setShowErrorOverlay}
        areAllFilesCompletedOrHaveErrors={areAllFilesCompletedOrHaveErrors}
      />,
      <LogEntriesEditManyModalPage
        selectedFile={selectedFile}
        logEntryIndex={logEntryIndex}
        setLogEntryIndex={setLogEntryIndex}
        logId={logId}
        organizationId={organizationId}
        projectId={projectId}
        isReadOnly={isReadOnly}
        forceShowErrors
        handleMapLogEntryFormValuesIntoLogEntries={
          handleMapLogEntryFormValuesIntoLogEntries
        }
        setLogEntries={setSelectedFileLogEntries}
        areAllFilesCompletedOrHaveErrors={areAllFilesCompletedOrHaveErrors}
        hasAnyErrorFiles={hasAnyErrorFiles}
        navigateToFileUploadErrorPage={navigateToFileUploadErrorPage}
      />,
      <LogEntriesTableReviewModalPage
        isSubmitting={isSubmitting}
        selectedFile={selectedFile}
        logId={logId}
        organizationId={organizationId}
        projectId={projectId}
        isReadOnly={isReadOnly}
        forceShowErrors
        setLogEntries={setSelectedFileLogEntries}
        logEntryIDsToDelete={logEntryIDsToDelete}
        setLogEntryIDsToDelete={setLogEntryIDsToDelete}
      />,
    ],
    [
      uploadedFiles,
      addFiles,
      handleUploadLogEntriesImage,
      removeFileData,
      showErrorOverlay,
      areAllFilesCompletedOrHaveErrors,
      selectedFile,
      logEntryIndex,
      setLogEntryIndex,
      logId,
      organizationId,
      projectId,
      isReadOnly,
      handleMapLogEntryFormValuesIntoLogEntries,
      setSelectedFileLogEntries,
      hasAnyErrorFiles,
      navigateToFileUploadErrorPage,
      isSubmitting,
      logEntryIDsToDelete,
    ],
  );

  const isNextButtonDisabled =
    !fileData.length ||
    (currentPageNumber === Pages.EDIT_ENTRIES_PAGE &&
      !selectedFile?.logEntries?.length) ||
    (currentPageNumber === Pages.FILE_UPLOAD_PAGE &&
      (showErrorOverlay || areAllFilesCompletedOrHaveErrors));

  const nextButtonText =
    currentPageNumber === Pages.EDIT_ENTRIES_PAGE
      ? 'Continue to entry summary'
      : undefined;

  const isLastReadyFile =
    statusCounts.ready === 1 && statusCounts.processing === 0;
  const hasErrorFilesToReview = isLastReadyFile && statusCounts.error > 0;

  let completionButtonText = 'Submit';
  if (areMultipleReadyFilesOrProcessing) {
    completionButtonText = 'Submit and go to next file';
  } else if (hasErrorFilesToReview) {
    completionButtonText = 'Submit and review errors';
  }

  if (!isOpen) return null;

  return (
    <StyledModalWizard
      addArrayMutators
      isEditing={false}
      isNextButtonDisabled={isNextButtonDisabled}
      onNextPage={onNextPage}
      renderActionsLeft={renderModalActionLeftButtons}
      itemLabel="Log Entries"
      onClose={isReadOnly ? onClose : onCloseWithConfirmation}
      onPageSubmit={handleSubmit}
      validateForm={validate}
      shouldBypassValidationOnNextPage
      allowPristineSubmit
      parentHandlesSuccessAndError
      showBackButton
      title="File Upload"
      inlineSubtitle={
        selectedFile ? getFileStatusText(selectedFile) : undefined
      }
      wizardPages={wizardPages}
      maxWidth="xl"
      hideSaveButton={isReadOnly}
      fullHeight
      modalId={`${USAGE_TRACKING_PREFIX}-log-entries-image-upload-modal`}
      nextButtonText={nextButtonText}
      completionButtonText={completionButtonText}
    />
  );
}
